/**
 * Detect if the current browser is an in-app/embedded WebView.
 * 
 * Google blocks OAuth sign-in from embedded browsers (WebViews) with:
 *   Error 403: disallowed_useragent
 * 
 * This happens when users open the app from LinkedIn, Instagram, Facebook,
 * Twitter/X, Apple Mail, or other apps that use an in-app browser.
 */

export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';

  // Common in-app browser signatures
  const inAppPatterns = [
    'FBAN',           // Facebook App
    'FBAV',           // Facebook App version
    'FB_IAB',         // Facebook In-App Browser
    'Instagram',      // Instagram
    'LinkedInApp',    // LinkedIn
    'Twitter',        // Twitter / X
    'Line/',          // LINE messenger
    'KAKAOTALK',      // KakaoTalk
    'SamsungBrowser/.*CrossApp', // Samsung cross-app
    'Snapchat',       // Snapchat
    'Pinterest',      // Pinterest
    'MicroMessenger', // WeChat
    'QQ/',            // QQ Browser
    'Bytedance',      // TikTok / ByteDance
    'musical_ly',     // TikTok (old)
    'TikTok',         // TikTok
  ];

  for (const pattern of inAppPatterns) {
    if (new RegExp(pattern, 'i').test(ua)) {
      return true;
    }
  }

  // iOS WebView detection:
  // - Real Safari has "Safari/" in the UA string
  // - iOS WebViews (SFSafariViewController / WKWebView) often lack "Safari/"
  //   but still have "AppleWebKit"
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  if (isIOS) {
    const hasSafari = /Safari\//.test(ua);
    const hasAppleWebKit = /AppleWebKit/.test(ua);
    // If it has AppleWebKit but NOT Safari, it's likely a WebView
    if (hasAppleWebKit && !hasSafari) {
      return true;
    }
  }

  // Android WebView detection:
  // - Android WebViews include "wv" in the UA string
  const isAndroidWebView = /; wv\)/.test(ua);
  if (isAndroidWebView) {
    return true;
  }

  return false;
}

/**
 * Get the name of the in-app browser (for user-friendly messaging).
 */
export function getInAppBrowserName() {
  if (typeof navigator === 'undefined') return 'this app';

  const ua = navigator.userAgent || '';

  if (/LinkedInApp/i.test(ua)) return 'LinkedIn';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'Facebook';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/Twitter/i.test(ua)) return 'Twitter/X';
  if (/TikTok|Bytedance|musical_ly/i.test(ua)) return 'TikTok';
  if (/Pinterest/i.test(ua)) return 'Pinterest';
  if (/Snapchat/i.test(ua)) return 'Snapchat';
  if (/MicroMessenger/i.test(ua)) return 'WeChat';
  if (/Line\//i.test(ua)) return 'LINE';

  return 'this app';
}

/**
 * Attempt to open the current URL in the system's default browser.
 * Different platforms require different approaches.
 */
export function openInSystemBrowser() {
  const url = window.location.href;

  // iOS: Use a custom intent-like approach  
  // Android: Try intent:// scheme to open Chrome
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);

  if (isAndroid) {
    // Android: Try Chrome intent
    const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
    window.location.href = intentUrl;
    // Fallback: just open in new tab after a short delay
    setTimeout(() => {
      window.open(url, '_system');
    }, 500);
  } else {
    // iOS and others: window.open with _blank may trigger system browser in some WebViews
    // Also try the x-safari scheme as a fallback
    window.open(url, '_blank');
  }
}
